/**
 * Client-side mapping from the patient API's relationship resolver fields into
 * moon's experience vocabulary.
 *
 * `dashboard-api` (PR #542) exposes the relationships model as RAW pg-enum
 * values with NO server-side display mapping — the client owns display. moon
 * already owns a richer experience vocabulary (`ViewerRole`, `JourneyStage`,
 * `ProcessContext`), so this module is the single place that relabels the raw
 * `participant_role` / `participant_status` enums into that vocabulary.
 *
 * Do NOT push any of this relabeling back to the server: the whole point of
 * #542 / #1341 is that the API stays raw and the client maps for display.
 */
import type { Patient } from "@louise/patients/patients.types";
import type {
  JourneyStage,
  ProcessContext,
  ViewerRole,
} from "./experience.types";

const DONOR_GOALS = new Set(["egg-donation", "sperm-donation"]);
const SURROGACY_GOALS = new Set(["surrogacy"]);

/**
 * Raw `participant_role` pg-enum from the patient API (`participantRole`),
 * derived from the generated OpenAPI schema — never hand-listed.
 */
export type ParticipantRole = NonNullable<Patient["participantRole"]>;

/**
 * Raw `participant_status` pg-enum from the patient API (`participantStatus`),
 * derived from the generated OpenAPI schema — never hand-listed.
 */
export type ParticipantStatus = NonNullable<Patient["participantStatus"]>;

/**
 * A single counterparty as exposed by the patient API (`counterparties[]`),
 * derived from the generated OpenAPI schema.
 */
export type CounterpartyInput = NonNullable<Patient["counterparties"]>[number];

/**
 * Whether the generated relationship fields identify a current Partner Home
 * context. Missing status is intentionally unresolved: a role without a
 * relationship state is not enough to expose the partner surface.
 */
export function isCurrentPartnerParticipation(
  role: ParticipantRole | null | undefined,
  status: ParticipantStatus | null | undefined,
): boolean {
  if (role !== "partner") return false;

  switch (status) {
    case "screening":
    case "approved":
    case "matched":
    case "legal-review":
    case "active-care":
    case "complete":
      return true;
    default:
      return false;
  }
}

/**
 * Map the raw `participant_role` into moon's `ViewerRole`.
 *
 * - `partner` / `donor` / `intended-parent` pass through (same labels).
 * - `gestational-carrier` → `carrier` (moon's shorter label for the same role).
 *
 * moon maps only the values it natively supports; the API enum may be a
 * superset (e.g. `recipient`) — unmapped values yield `null` and the caller
 * keeps its existing treatment-derived derivation, rather than mislabeling.
 * (The API being a superset of what the app supports is fine; only the reverse
 * — the app expecting a value the API lacks — would be a problem.) So
 * `recipient`, and any future role not in moon's `ViewerRole`, return `null`.
 */
export function participantRoleToViewerRole(
  role: string | null | undefined,
): ViewerRole | null {
  switch (role) {
    case "partner":
      return "partner";
    case "donor":
      return "donor";
    case "intended-parent":
      return "intended-parent";
    case "gestational-carrier":
      return "carrier";
    default:
      return null;
  }
}

/**
 * Keep relationship records durable while preventing a role from a previous
 * goal from routing the active experience. Goal corrections intentionally do
 * not rewrite historical participant metadata, so every display consumer
 * must use this goal-aware projection instead of the raw role.
 */
export function resolveActiveViewerRole(
  treatmentGoal: string | null | undefined,
  role: ViewerRole | null | undefined,
): ViewerRole | null {
  if (role === "donor") {
    return DONOR_GOALS.has(treatmentGoal ?? "") ? role : null;
  }

  if (role === "carrier" || role === "intended-parent") {
    return SURROGACY_GOALS.has(treatmentGoal ?? "") ? role : null;
  }

  return role ?? null;
}

export function isHelperViewerRole(
  role: ViewerRole | null | undefined,
): boolean {
  return role === "donor" || role === "carrier" || role === "intended-parent";
}

/**
 * Map the raw `participant_status` into moon's `JourneyStage` where there is a
 * clear equivalent.
 *
 * - `screening` / `matched` / `approved` / `donating` / `complete` map to the
 *   identically named moon journey stages.
 * - `active-care` → `in-care` (moon's label for being in active treatment).
 *
 * moon maps only the values it natively supports; the API enum may be a
 * superset (e.g. `ended`, `legal-review`) — unmapped values yield `null` and
 * the caller keeps its existing treatment-derived stage, rather than
 * mislabeling:
 * - `ended` has no moon `JourneyStage` equivalent → `null`.
 * - `legal-review` is a process, not a journey stage → `null` here; it is
 *   surfaced as the `legal` ProcessContext instead (see
 *   `participantStatusToProcessContext`).
 *
 * moon journey stages with no backend `participant_status` source — `explore`,
 * `before-treatment`, `time-to-wait`, `stimulation`, `retrieval`, `unknown` —
 * are intentionally never produced here; they continue to come from moon's
 * treatment/cycle-driven derivation.
 */
export function participantStatusToJourneyStage(
  status: ParticipantStatus | null | undefined,
): JourneyStage | null {
  switch (status) {
    case "screening":
      return "screening";
    case "matched":
      return "matched";
    case "approved":
      return "approved";
    case "donating":
      return "donating";
    case "complete":
      return "complete";
    case "active-care":
      return "in-care";
    default:
      return null;
  }
}

/**
 * Statuses that are processes rather than journey stages map to a moon
 * `ProcessContext` badge instead of a journey stage.
 *
 * - `legal-review` → `legal` (the legal/contract step of a donor / surrogacy
 *   arrangement).
 * - `matched` → `matching` as well as its journey stage, so matched-stage
 *   relationships still surface the matching process badge.
 *
 * Returns `null` when the status has no associated process context.
 */
export function participantStatusToProcessContext(
  status: ParticipantStatus | null | undefined,
): ProcessContext | null {
  switch (status) {
    case "legal-review":
      return "legal";
    case "matched":
      return "matching";
    default:
      return null;
  }
}

/**
 * The lead counterparty, if any — the patient who anchors the relationship
 * (e.g. the intended parent a carrier / donor is matched with). Falls back to
 * the first counterparty when none is explicitly flagged as lead.
 */
export function leadCounterparty(
  counterparties: CounterpartyInput[] | null | undefined,
): CounterpartyInput | null {
  if (!counterparties || counterparties.length === 0) return null;
  return counterparties.find((c) => c.isLead) ?? counterparties[0] ?? null;
}
